/**
 * Admin Dashboard — Ads Charts Tab (lazy-loaded on first tab click).
 * Fetches Facebook/Instagram/Pinterest/Microsoft daily metrics via AdminDashboardAPI.
 * Two-tier caching: sessionStorage (fast, daily-aware via yesterday-validation) + server (persistent KV).
 * sessionStorage invalidates daily by comparing cached.until to UTC yesterday; also invalidates if Pinterest/MS
 * credentials become available (env vars added during session). Renders charts via Chart.js (already on page).
 */
(function () {
  'use strict';

  const SESSION_KEY = 'adscharts_v3_'; // v3: adds Pinterest

  // Gate all observability logs behind this flag — delegate to core when available (single source of truth).
  // Local fallback covers the unlikely case this module loads before core.js.
  const isDev =
    window.AdminDashboardCore?.isDev ??
    (window.location.hostname === 'localhost' || window.location.search.includes('dev=true'));

  // Platform-specific chart colors (hex codes)
  const PLATFORM_COLORS = {
    facebook: '#4f94f0', // Google blue
    google: '#e2607a', // Google red-pink
    microsoft: '#34a853', // Google green
    pinterest: '#e60023', // Pinterest red
  };

  // Chart text color constants with opacity tiers
  const CHART_TEXT_COLORS = {
    legendBox: 'rgba(255,255,255,0.75)',
    axisLabel: 'rgba(255,255,255,0.85)',
    tickLabel: 'rgba(255,255,255,0.45)',
    gridLine: 'rgba(255,255,255,0.05)',
    tooltip: 'rgba(255,255,255,0.4)',
    crosshair: 'rgba(255,255,255,0.45)',
  };

  // UTC "yesterday" (YYYY-MM-DD), recomputed on every call.
  //
  // This was memoized once per page load, on the stated premise that "a page load
  // always corresponds to a single calendar day" and that the value "cannot change
  // without a page reload". A tab left open across midnight is exactly the incident
  // this cache guard was added for, and under the memo that tab kept comparing
  // against the yesterday it had computed at load time, so an entry written before
  // midnight still matched and the charts stayed frozen at the old date. Reaching it
  // takes one change of the period <select>, which calls _load() with no reload and
  // no cache bust. Two Date operations per user-initiated _load is not worth
  // defeating the guard for.
  function _getYesterdayUntil() {
    const yest = new Date();
    yest.setUTCDate(yest.getUTCDate() - 1);
    return yest.toISOString().slice(0, 10);
  }

  // Shared metric IDs used by both Google and Microsoft summary cards.
  // Declared once at module scope to avoid re-creating the array on every
  // _renderSummary() call (previously declared twice per call as `ids` + `msIds`).
  const SUMMARY_METRIC_IDS = [
    'roas',
    'spend',
    'conversions',
    'purchase-value',
    'impressions',
    'ctr',
    'cost-conv',
  ];

  window.AdminDashboardAdsCharts = {
    _charts: {}, // active Chart.js instances keyed by canvas id
    _initialized: false,
    _loading: false,
    _currentDays: 30,

    /* ------------------------------------------------------------------ */
    /* Public entry-point — called once when the tab is first opened        */
    /* ------------------------------------------------------------------ */
    init() {
      if (this._initialized) return;
      this._initialized = true;
      this._bindControls();
      this._load(30);
    },

    /* ------------------------------------------------------------------ */
    /* Event wiring                                                          */
    /* ------------------------------------------------------------------ */
    _bindControls() {
      const sel = document.getElementById('ads-charts-period');
      if (sel) {
        sel.addEventListener('change', (e) => {
          this._load(parseInt(e.target.value, 10));
        });
      }

      const btn = document.getElementById('ads-charts-refresh');
      if (btn) {
        btn.addEventListener('click', () => {
          // Bust session cache for current period and reload
          sessionStorage.removeItem(SESSION_KEY + this._currentDays);
          this._load(this._currentDays);
        });
      }
    },

    /* ------------------------------------------------------------------ */
    /* Data loading                                                          */
    /* ------------------------------------------------------------------ */
    /**
     * Load ads metrics for given period — two-tier cache: sessionStorage (fast, date-aware) + server (persistent).
     * sessionStorage cache is **invalidated daily** by comparing cached.until to yesterday (UTC).
     * Also invalidates if Pinterest/Microsoft credentials become available (env var update during session).
     * Falls back to server fetch if cache is stale or not configured.
     */
    async _load(days) {
      if (this._loading) return;
      this._loading = true;
      this._currentDays = days;
      this._hideError();
      this._setLoading(true);

      const t0 = Date.now();
      try {
        // 1. Check sessionStorage — instant within the same browser session.
        // Validate that cached 'until' still matches yesterday (UTC); discard if stale.
        // Without this check a tab left open across days would show charts frozen at the
        // date when it was first loaded (e.g. "4/14" instead of today - 1).
        const cacheKey = SESSION_KEY + days;
        const raw = sessionStorage.getItem(cacheKey);
        if (raw) {
          try {
            const cached = JSON.parse(raw);
            const expectedUntil = _getYesterdayUntil();
            // Also invalidate if Pinterest had no credentials — Vercel env vars may have
            // been added since this was cached, and we want to pick that up immediately.
            const ptUnconfigured =
              cached.pinterestStatus === 'no_credentials' ||
              cached.pinterestStatus === 'not_configured';
            const msUnconfigured = cached.microsoftStatus === 'not_configured';
            if (cached.until === expectedUntil && !ptUnconfigured && !msUnconfigured) {
              if (isDev) {
                console.debug(
                  `[AdsCharts] sessionStorage cache hit (days=${days} until=${cached.until} fb=${Array.isArray(cached.facebook) ? cached.facebook.length : 'n/a'} pt=${cached.pinterestStatus || 'ok'} ms=${cached.microsoftStatus || 'ok'})`
                );
              }
              this._render(cached);
              this._setLoading(false);
              this._loading = false;
              return;
            }
            // Stale or platform unconfigured — remove and fall through to server fetch
            if (isDev) {
              const staleReason =
                cached.until !== expectedUntil
                  ? `until mismatch (cached=${cached.until} expected=${expectedUntil})`
                  : ptUnconfigured
                    ? `pinterest unconfigured (${cached.pinterestStatus})`
                    : `microsoft unconfigured (${cached.microsoftStatus})`;
              console.debug(
                `[AdsCharts] sessionStorage cache miss (days=${days} reason=${staleReason})`
              );
            }
            sessionStorage.removeItem(cacheKey);
          } catch (_) {
            if (isDev) {
              console.warn(
                '[AdsCharts] sessionStorage parse error — discarding cache for key',
                cacheKey
              );
            }
            sessionStorage.removeItem(cacheKey);
          }
        } else if (isDev) {
          console.debug(`[AdsCharts] sessionStorage cache miss (days=${days} reason=no entry)`);
        }

        // 2. Fetch from server — uses KV/in-memory cache on the server side
        if (isDev) console.debug(`[AdsCharts] fetching from server (days=${days})`);
        const api = window.AdminDashboardAPI;
        let result;
        if (api) {
          result = await api.fetchDashboardData(
            `endpoint=ads-daily-metrics&days=${days}`,
            'ads-daily-metrics'
          );
        } else {
          // Fallback if API module isn't ready yet
          const url =
            (window.AdminDashboard?.config?.apiProxyUrl || '/apps/proxy') +
            `?endpoint=ads-daily-metrics&days=${days}`;
          const resp = await fetch(url);
          const json = await resp.json();
          result =
            json.success && json.data !== null && typeof json.data === 'object'
              ? { success: true, data: json.data }
              : { success: false, error: json.error || 'Invalid response shape' };
        }

        if (result.success && result.data && Array.isArray(result.data.facebook)) {
          // Don't cache when Pinterest credentials are absent — Vercel env vars may be
          // added between page loads and we need to detect that immediately.
          const ptOk =
            result.data.pinterestStatus !== 'no_credentials' &&
            result.data.pinterestStatus !== 'not_configured';
          const msOk = result.data.microsoftStatus !== 'not_configured';
          if (ptOk && msOk) {
            sessionStorage.setItem(cacheKey, JSON.stringify(result.data));
            if (isDev) {
              console.debug(
                `[AdsCharts] server fetch complete — cached in sessionStorage (days=${days} ms=${Date.now() - t0})`
              );
            }
          } else if (isDev) {
            console.debug(
              `[AdsCharts] server fetch complete — NOT cached (days=${days} pt=${result.data.pinterestStatus} ms=${result.data.microsoftStatus} ms_elapsed=${Date.now() - t0})`
            );
          }
          this._render(result.data);
        } else {
          if (isDev) console.warn('[AdsCharts] server fetch returned no usable data', result.error);
          this._showError(result.error || 'No data returned from server');
        }
      } catch (err) {
        if (isDev) console.error('[AdsCharts] _load error:', err);
        this._showError('Error loading ads data: ' + (err.message || err));
      }

      this._setLoading(false);
      this._loading = false;
    },

    /* ------------------------------------------------------------------ */
    /* Rendering orchestrator                                                */
    /* ------------------------------------------------------------------ */
    _render(data) {
      const {
        facebook: rawFb = [],
        instagram: rawIg = [],
        google: rawGg = [],
        googleStatus = 'ok',
        pinterest: rawPt = [],
        pinterestStatus = 'ok',
        microsoft: rawMs = [],
        microsoftStatus = 'ok',
        days,
        since,
        until,
      } = data;
      // Coerce to arrays in case the API returns null/non-array for a platform
      const facebook = Array.isArray(rawFb) ? rawFb : [];
      const instagram = Array.isArray(rawIg) ? rawIg : [];
      const google = Array.isArray(rawGg) ? rawGg : [];
      const pinterest = Array.isArray(rawPt) ? rawPt : [];
      const microsoft = Array.isArray(rawMs) ? rawMs : [];
      this._updateNote(days, since, until);
      this._renderSummary(
        facebook,
        instagram,
        google,
        googleStatus,
        pinterest,
        pinterestStatus,
        microsoft,
        microsoftStatus
      );
      this._renderCharts(facebook, instagram, google, pinterest);
    },

    _updateNote(days, since, until) {
      const el = document.getElementById('ads-charts-period-note');
      if (!el) return;
      const fmt = (d) => {
        if (!d) return '—';
        const [y, m, day] = d.split('-');
        return `${parseInt(m, 10)}/${parseInt(day, 10)}/${y}`;
      };
      el.textContent = `Showing ${days} days: ${fmt(since)} – ${fmt(until)} (today excluded)`;
    },

    /* ------------------------------------------------------------------ */
    /* Summary cards                                                         */
    /* ------------------------------------------------------------------ */
    _renderSummary(
      fb,
      ig,
      gg,
      googleStatus = 'ok',
      pt = [],
      pinterestStatus = 'ok',
      ms = [],
      microsoftStatus = 'ok'
    ) {
      // Hide Facebook/Instagram platform cards if not configured (no data)
      const fbCard = document.querySelector('.ads-summary-platform.fb');
      const igCard = document.querySelector('.ads-summary-platform.ig');
      if (fbCard) fbCard.style.display = fb && fb.length > 0 ? '' : 'none';
      if (igCard) igCard.style.display = ig && ig.length > 0 ? '' : 'none';

      this._fillSummary('fb', this._summarize(fb));
      this._fillSummary('ig', this._summarize(ig));

      const ggHeader = document.querySelector('.ads-summary-platform.gg h4');
      const ggNote = document.getElementById('gg-status-note');

      if (googleStatus === 'ok') {
        this._fillSummary('gg', this._summarize(gg));
        if (ggHeader) ggHeader.title = '';
        if (ggNote) ggNote.textContent = '';
      } else if (googleStatus === 'no_data') {
        SUMMARY_METRIC_IDS.forEach((id) => {
          const el = document.getElementById(`gg-sum-${id}`);
          if (el) el.textContent = '—';
        });
        if (ggHeader) ggHeader.title = 'No Google Ads campaign activity found for this period';
        if (ggNote) ggNote.textContent = '(no campaigns)';
      } else if (googleStatus === 'no_credentials') {
        SUMMARY_METRIC_IDS.forEach((id) => {
          const el = document.getElementById(`gg-sum-${id}`);
          if (el) el.textContent = '—';
        });
        if (ggHeader) ggHeader.title = 'Google Ads not configured (missing API credentials)';
        if (ggNote) ggNote.textContent = '(not configured)';
      } else {
        SUMMARY_METRIC_IDS.forEach((id) => {
          const el = document.getElementById(`gg-sum-${id}`);
          if (el) el.textContent = '—';
        });
        const errText = googleStatus.replace(/^error:/, '').trim();
        if (ggHeader) ggHeader.title = errText;
        if (ggNote) ggNote.textContent = '(error — hover for details)';
      }

      // Pinterest summary card
      const ptHeader = document.querySelector('.ads-summary-platform.pt h4');
      const ptNote = document.getElementById('pt-status-note');

      if (pinterestStatus === 'ok_checkout' || pinterestStatus === 'ok') {
        this._fillSummary('pt', this._summarize(pt));
        if (ptHeader) ptHeader.title = '';
        if (ptNote) ptNote.textContent = '';
        const ptConvLabel = document.getElementById('pt-conv-label');
        const ptPvLabel = document.getElementById('pt-pv-label');
        if (pinterestStatus === 'ok_checkout') {
          // Real click-attributed checkout data — show accurate labels
          if (ptConvLabel) {
            ptConvLabel.textContent = 'Avg Checkouts / day';
            ptConvLabel.title =
              'Click-attributed web checkouts (users who clicked your ad then completed checkout)';
          }
          if (ptPvLabel) {
            ptPvLabel.title = 'Total purchase value from click-attributed web checkouts';
            ptPvLabel.textContent = 'Avg Purchase Value / day';
          }
        } else {
          // No checkout pixel yet — purchase value and ROAS not available
          const ptRoasEl = document.getElementById('pt-sum-roas');
          const ptPvEl = document.getElementById('pt-sum-purchase-value');
          if (ptRoasEl) {
            ptRoasEl.textContent = 'N/A';
            ptRoasEl.title =
              'Purchase value not available — install Pinterest checkout pixel first';
            ptRoasEl.classList.remove('roas-low', 'roas-below', 'roas-top');
          }
          if (ptPvEl) {
            ptPvEl.textContent = 'N/A';
            ptPvEl.title = 'Purchase value not available — install Pinterest checkout pixel first';
          }
        }
      } else if (pinterestStatus === 'no_data') {
        SUMMARY_METRIC_IDS.forEach((id) => {
          const el = document.getElementById(`pt-sum-${id}`);
          if (el) el.textContent = '—';
        });
        if (ptHeader) ptHeader.title = 'Pinterest campaigns found but no spend data in this period';
        if (ptNote) ptNote.textContent = '(no spend data)';
      } else if (pinterestStatus === 'no_credentials' || pinterestStatus === 'not_configured') {
        SUMMARY_METRIC_IDS.forEach((id) => {
          const el = document.getElementById(`pt-sum-${id}`);
          if (el) el.textContent = '—';
        });
        if (ptHeader) ptHeader.title = 'Pinterest Ads not configured (missing API credentials)';
        if (ptNote) ptNote.textContent = '(not configured)';
      } else {
        SUMMARY_METRIC_IDS.forEach((id) => {
          const el = document.getElementById(`pt-sum-${id}`);
          if (el) el.textContent = '—';
        });
        const errText = pinterestStatus.replace(/^error:/, '').trim();
        if (ptHeader) ptHeader.title = errText;
        if (ptNote) ptNote.textContent = '(error — hover for details)';
      }

      // Microsoft Ads summary card
      const msHeader = document.querySelector('.ads-summary-platform.ms h4');
      const msNote = document.getElementById('ms-status-note');

      if (microsoftStatus === 'ok') {
        this._fillSummary('ms', this._summarize(ms));
        if (msHeader) msHeader.title = '';
        if (msNote) msNote.textContent = '';
      } else if (microsoftStatus === 'no_data' || microsoftStatus === 'not_configured') {
        SUMMARY_METRIC_IDS.forEach((id) => {
          const el = document.getElementById(`ms-sum-${id}`);
          if (el) el.textContent = '—';
        });
        if (msHeader) {
          msHeader.title =
            microsoftStatus === 'not_configured'
              ? 'Microsoft Ads not configured — run the orchestrator once to generate data'
              : 'No Microsoft Ads spend data for this period';
        }
        if (msNote) {
          msNote.textContent =
            microsoftStatus === 'not_configured' ? '(not configured)' : '(no data)';
        }
      } else {
        SUMMARY_METRIC_IDS.forEach((id) => {
          const el = document.getElementById(`ms-sum-${id}`);
          if (el) el.textContent = '—';
        });
        const errText = microsoftStatus.replace(/^error:/, '').trim();
        if (msHeader) msHeader.title = errText;
        if (msNote) msNote.textContent = '(error — hover for details)';
      }
    },

    _summarize(arr) {
      const n = arr.length;
      if (!n) return {};
      const sum = (k) => arr.reduce((s, d) => s + (Number(d[k]) || 0), 0);
      const totalSpend = sum('spend');
      const totalImpressions = sum('impressions');
      const totalClicks = sum('clicks');
      const totalConversions = sum('conversions');
      const totalPurchaseValue = sum('purchaseValue');
      return {
        spend: totalSpend / n,
        impressions: Math.round(totalImpressions / n),
        ctr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
        conversions: totalConversions / n,
        purchaseValue: totalPurchaseValue / n,
        roas: totalSpend > 0 ? totalPurchaseValue / totalSpend : 0,
        costPerConversion: totalConversions > 0 ? totalSpend / totalConversions : 0,
      };
    },

    _fillSummary(prefix, s) {
      const set = (id, val) => {
        const el = document.getElementById(`${prefix}-sum-${id}`);
        if (el) el.textContent = val;
      };
      if (!Object.keys(s).length) {
        [
          'roas',
          'spend',
          'conversions',
          'purchase-value',
          'impressions',
          'ctr',
          'cost-conv',
        ].forEach((id) => set(id, '—'));
        return;
      }
      set('roas', this._fmt(s.roas, 'x2'));
      // Color ROAS: industry avg 3.5×, top tier 5×
      const roasEl = document.getElementById(`${prefix}-sum-roas`);
      if (roasEl) {
        roasEl.classList.remove('roas-low', 'roas-below', 'roas-top');
        if (s.roas > 0) {
          if (s.roas < 2) {
            roasEl.classList.add('roas-low');
          } // poor — orange
          else if (s.roas < 3.5) {
            roasEl.classList.add('roas-below');
          } // below avg — amber
          else if (s.roas >= 5) roasEl.classList.add('roas-top'); // top tier — bright green
          // 3.5–5×: at/above avg — keeps default highlight green
        }
      }
      set('spend', this._fmt(s.spend, '$'));
      set('conversions', this._fmt(s.conversions, '.1'));
      set('purchase-value', this._fmt(s.purchaseValue, '$'));
      set('impressions', this._fmtNum(s.impressions));
      set('ctr', this._fmt(s.ctr, '%3'));
      set('cost-conv', s.conversions > 0 ? this._fmt(s.costPerConversion, '$') : 'N/A');
    },

    /* ------------------------------------------------------------------ */
    /* Charts — DRY helpers                                                 */
    /* ------------------------------------------------------------------ */
    /**
     * Helper: Extract metric values from data array for a given key
     * Reduces repetitive map-and-normalize pattern
     *
     * @param {Array<object>} data - Data array (or falsy for empty)
     * @param {string} key - Field name to extract
     * @returns {Array<number>} Normalized numeric values
     */
    _extractMetricValues(data, key) {
      return (data || []).map((d) => Number(d[key]) || 0);
    },

    /**
     * Helper: Compute cumulative (running) average
     * Used to generate trend lines for each platform
     *
     * @param {Array<number>} vals - Raw metric values
     * @returns {Array<number>} Cumulative average at each point
     */
    _computeCumulativeAverage(vals) {
      return vals.map((_, i) => {
        const slice = vals.slice(0, i + 1);
        return slice.reduce((a, b) => a + b, 0) / slice.length;
      });
    },

    /**
     * Helper: Create a filled dataset config for a platform metric
     * Reduces duplication for the 4 main platform line datasets
     *
     * @param {string} label - Dataset label (e.g., 'Facebook')
     * @param {Array<number>} data - Values to plot
     * @param {string} borderColor - Hex/rgba color for line
     * @param {string} backgroundColor - Hex/rgba color for fill
     * @param {number} pointRadius - Radius for data points (0 to hide)
     * @param {boolean} fill - Whether to fill area under line
     * @returns {object} Chart.js dataset configuration
     */
    _createFilledDataset(label, data, borderColor, backgroundColor, pointRadius, fill = true) {
      return {
        label,
        data,
        borderColor,
        backgroundColor,
        borderWidth: 2,
        pointRadius,
        pointHoverRadius: 4,
        tension: 0.35,
        fill,
      };
    },

    /**
     * Helper: Create a dashed average line dataset config
     * Reduces duplication for the 4 average trend lines
     *
     * @param {string} label - Dataset label (e.g., 'FB avg')
     * @param {Array<number>} data - Cumulative average values
     * @param {string} borderColor - Hex/rgba color for line (semi-transparent)
     * @returns {object} Chart.js dataset configuration
     */
    _createAverageDataset(label, data, borderColor) {
      return {
        label,
        data,
        borderColor,
        borderWidth: 1.5,
        borderDash: [5, 4],
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: false,
      };
    },

    /* ------------------------------------------------------------------ */
    /* Charts                                                                */
    /* ------------------------------------------------------------------ */
    _renderCharts(fb, ig, gg, pt = []) {
      // Defer if Chart.js hasn't finished loading (it's deferred in the page)
      if (!window.Chart) {
        setTimeout(() => this._renderCharts(fb, ig, gg, pt), 400);
        return;
      }

      // Use whichever non-empty series is longest for x-axis labels
      const labelSource = [fb, ig, gg, pt].reduce(
        (best, arr) => (arr.length > best.length ? arr : best),
        []
      );
      const labels = labelSource.map((d) => {
        const [, m, day] = (d.date || '').split('-');
        return `${parseInt(m, 10)}/${parseInt(day, 10)}`;
      });

      // [canvasId, title, dataKey, yAxisLabel, valueFormatter]
      const configs = [
        {
          id: 'chart-roas',
          title: 'ROAS',
          key: 'roas',
          yLabel: 'ROAS',
          fmt: (v) => this._fmt(v, 'x2'),
        },
        {
          id: 'chart-spend',
          title: 'Total Spend ($)',
          key: 'spend',
          yLabel: '$/day',
          fmt: (v) => this._fmt(v, '$'),
        },
        {
          id: 'chart-purchase-value',
          title: 'Purchase Value ($)',
          key: 'purchaseValue',
          yLabel: '$/day',
          fmt: (v) => this._fmt(v, '$'),
        },
        {
          id: 'chart-conversions',
          title: 'Conversions',
          key: 'conversions',
          yLabel: 'conversions',
          fmt: (v) => v.toFixed(0),
        },
        {
          id: 'chart-impressions',
          title: 'Impressions',
          key: 'impressions',
          yLabel: 'impressions',
          fmt: (v) => this._fmtNum(Math.round(v)),
        },
        {
          id: 'chart-ctr',
          title: 'CTR (%)',
          key: 'ctr',
          yLabel: '% CTR',
          fmt: (v) => this._fmt(v, '%3'),
        },
        {
          id: 'chart-cost-conv',
          title: 'Cost per Conversion ($)',
          key: 'costPerConversion',
          yLabel: '$/conv',
          fmt: (v) => this._fmt(v, '$'),
        },
      ];

      configs.forEach((cfg) => this._buildChart(cfg, labels, fb, ig, gg, pt));
    },

    _buildChart(cfg, labels, fbData, igData, ggData, ptData = []) {
      const canvas = document.getElementById(cfg.id);
      if (!canvas) return;

      // Destroy previous instance to free memory
      if (this._charts[cfg.id]) {
        this._charts[cfg.id].destroy();
        delete this._charts[cfg.id];
      }

      // Extract metric values using DRY helper
      const fbVals = this._extractMetricValues(fbData, cfg.key);
      const igVals = this._extractMetricValues(igData, cfg.key);
      const ggVals = this._extractMetricValues(ggData, cfg.key);
      const ptVals = this._extractMetricValues(ptData, cfg.key);

      // Compute averages using DRY helper
      const fbAvg = this._computeCumulativeAverage(fbVals);
      const igAvg = this._computeCumulativeAverage(igVals);
      const ggAvg = this._computeCumulativeAverage(ggVals);
      const ptAvg = this._computeCumulativeAverage(ptVals);
      const pointRadius = labels.length > 60 ? 0 : 2;

      // Build datasets using DRY helpers
      const datasets = [
        this._createFilledDataset(
          'Facebook',
          fbVals,
          PLATFORM_COLORS.facebook,
          'rgba(79,148,240,0.08)',
          pointRadius
        ),
        this._createFilledDataset(
          'Instagram',
          igVals,
          PLATFORM_COLORS.google,
          'rgba(226,96,122,0.08)',
          pointRadius
        ),
        this._createFilledDataset(
          'Google',
          ggVals,
          PLATFORM_COLORS.microsoft,
          'rgba(52,168,83,0.08)',
          pointRadius
        ),
        this._createFilledDataset(
          'Pinterest',
          ptVals,
          PLATFORM_COLORS.pinterest,
          'rgba(230,0,35,0.08)',
          pointRadius
        ),
        this._createAverageDataset('FB avg', fbAvg, 'rgba(79,148,240,0.5)'),
        this._createAverageDataset('IG avg', igAvg, 'rgba(226,96,122,0.5)'),
        this._createAverageDataset('GG avg', ggAvg, 'rgba(52,168,83,0.5)'),
        this._createAverageDataset('PT avg', ptAvg, 'rgba(230,0,35,0.5)'),
      ];

      this._charts[cfg.id] = new Chart(canvas, {
        type: 'line',
        data: {
          labels,
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              position: 'top',
              labels: { boxWidth: 12, padding: 12, color: CHART_TEXT_COLORS.legendBox },
            },
            title: {
              display: true,
              text: cfg.title,
              color: CHART_TEXT_COLORS.axisLabel,
              font: { size: 13, weight: '600' },
            },
            tooltip: {
              backgroundColor: 'rgba(20,20,30,0.92)',
              titleColor: 'rgba(255,255,255,0.9)',
              bodyColor: 'rgba(255,255,255,0.75)',
              callbacks: {
                label: (ctx) => {
                  const raw = ctx.raw;
                  return ` ${ctx.dataset.label}: ${raw !== null && raw !== undefined ? cfg.fmt(raw) : '—'}`;
                },
              },
            },
          },
          scales: {
            x: {
              ticks: { color: CHART_TEXT_COLORS.tickLabel, maxTicksLimit: 12, maxRotation: 45 },
              grid: { color: CHART_TEXT_COLORS.gridLine },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: cfg.yLabel,
                color: CHART_TEXT_COLORS.tooltip,
                font: { size: 11 },
              },
              ticks: {
                color: CHART_TEXT_COLORS.crosshair,
                callback: (val) => cfg.fmt(val),
              },
              grid: { color: CHART_TEXT_COLORS.gridLine },
            },
          },
        },
      });
    },

    /* ------------------------------------------------------------------ */
    /* UI state helpers                                                      */
    /* ------------------------------------------------------------------ */
    _setLoading(on) {
      const loadingEl = document.getElementById('ads-charts-loading');
      const container = document.getElementById('ads-charts-container');
      if (loadingEl) loadingEl.style.display = on ? 'flex' : 'none';
      if (container) container.style.opacity = on ? '0.35' : '1';
    },

    _showError(msg) {
      const el = document.getElementById('ads-charts-error');
      if (el) {
        el.textContent = msg;
        el.style.display = 'block';
      }
    },

    _hideError() {
      const el = document.getElementById('ads-charts-error');
      if (el) el.style.display = 'none';
    },

    /* ------------------------------------------------------------------ */
    /* Formatters                                                            */
    /* ------------------------------------------------------------------ */
    _fmt(n, mode) {
      n = Number(n) || 0;
      // '$' mode delegates to core.formatCurrency for locale-aware thousands separators.
      // Other modes keep their specialized output (x2 multiplier, %3 precision, .1 decimal).
      if (mode === '$') return window.AdminDashboardCore?.formatCurrency(n) ?? '$' + n.toFixed(2);
      if (mode === 'x2') return n.toFixed(2) + 'x';
      if (mode === '%3') return n.toFixed(3) + '%';
      if (mode === '.1') return n.toFixed(1);
      return n.toString();
    },

    _fmtNum(n) {
      // Delegate to core.formatNumber(n, 0) for consistent locale-aware formatting.
      return window.AdminDashboardCore?.formatNumber(n || 0, 0) ?? (n || 0).toLocaleString();
    },

    /**
     * Destroy all active Chart.js instances in the ads-charts registry.
     * Called by the page-lifecycle cleanup handler (beforeunload / visibilitychange)
     * to release canvas references and animation frames and prevent memory leaks.
     */
    destroyAll() {
      Object.keys(this._charts).forEach((id) => {
        try {
          this._charts[id].destroy();
        } catch (_e) {
          // Ignore errors during cleanup — chart may already be detached
        }
        delete this._charts[id];
      });
    },
  };
})();
